import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@xstate/react';
import {
  Box,
  Button,
  Card,
  Modal,
  Stepper,
} from '@chargify/custom-ui-components';

import { addConfigurationSelectors } from '../../../../machines/addOnsConfiguration';
import { useAddonsConfigurationService } from '../../../../hooks';
import { ComponentAllocationsContainer as ComponentsAllocationContainer } from '../../../../containers/ComponentsAllocationContainer';

type ComponentsAllocationContentProps = {
  /**
   * Modal display mode
   */
  displayMode: 'layer' | 'window';
  /**
   * Subscription ID
   */
  subscriptionId: number;
  /**
   * Close event handler
   */
  onClose: () => void;
  /**
   * Product catalog identifier
   */
  catalogId: number;
};

const Content = ({
  onClose,
  displayMode,
  subscriptionId,
  catalogId,
}: ComponentsAllocationContentProps) => {
  const { t } = useTranslation(['subscriptions-manager']);
  const addOnsConfigurationService = useAddonsConfigurationService(catalogId);

  const {
    configureAllocationsStep,
    previewAllocationsStep,
    previewAllocationsLoading,
    updateAllocationsLoading,
  } = useSelector(
    addOnsConfigurationService,
    addConfigurationSelectors.getAddonsState
  );

  const allocations = useSelector(
    addOnsConfigurationService,
    addConfigurationSelectors.getAllocations
  );

  const getCurrentStep = () => (previewAllocationsStep ? 1 : 0);

  useEffect(() => {
    addOnsConfigurationService.send('INITIALIZE', {
      subscriptionId,
    });
  }, []);

  return (
    <Card border={displayMode === 'layer' ? 'sharp' : 'rounded'}>
      <Card.Header>
        <Modal.Title
          onClose={onClose}
          title={t('components_allocations.configure_add_ons')}
        />
      </Card.Header>
      <Card.Body>
        <Box marginBottom={9}>
          <Stepper
            steps={[
              t('components_allocations.modal_edit_addons'),
              t('components_allocations.modal_preview_charges'),
            ]}
            currentStep={getCurrentStep()}
            variant="horizontal"
          />
        </Box>
        <ComponentsAllocationContainer
          subscriptionId={subscriptionId}
          addOnsConfigurationService={addOnsConfigurationService}
          onClose={onClose}
        />
      </Card.Body>
      <Card.Footer>
        <Button.Group alignment="right">
          {previewAllocationsStep && (
            <Box marginRight="auto">
              <Button
                color="secondary"
                label={t('go_back', { ns: 'common' })}
                iconLeft="chevronLeft"
                onClick={() => {
                  addOnsConfigurationService.send('GO_BACK');
                }}
              />
            </Box>
          )}
          <Button
            color="secondary"
            label={t('cancel', { ns: 'common' })}
            onClick={onClose}
          />
          {configureAllocationsStep && (
            <Button
              label={t('components_allocations.go_to_preview', {
                ns: 'subscriptions-manager',
              })}
              disabled={previewAllocationsLoading || allocations.length === 0}
              isLoading={previewAllocationsLoading}
              loadingText={t('processing', { ns: 'common' })}
              onClick={() =>
                addOnsConfigurationService.send('PREVIEW_ALLOCATIONS', {
                  allocations,
                })
              }
            />
          )}
          {previewAllocationsStep && (
            <Button
              label={t('components_allocations.confirm_changes', {
                ns: 'subscriptions-manager',
              })}
              disabled={updateAllocationsLoading}
              isLoading={updateAllocationsLoading}
              loadingText={t('processing', { ns: 'common' })}
              onClick={() => {
                addOnsConfigurationService.send('UPDATE_ALLOCATIONS', {
                  allocations,
                });
              }}
            />
          )}
        </Button.Group>
      </Card.Footer>
    </Card>
  );
};

export default Content;
